import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpService } from '../services/http/http.service';
import { ToasterService } from '../services/toaster/toaster.service';
import { UiService } from '../services/uiloader/uiloader.service';
import { CommonService } from '../services/common/common.service';
import { config } from '../config';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.sass']
})
export class SignupComponent implements OnInit {
  // Form states
  currentStep: number = 1;
  emailForm: FormGroup = new FormGroup({});
  signupForm: FormGroup = new FormGroup({});
  
  // State variables
  emailExists: boolean = false;
  isEmailValidated: boolean = false;
  isLoading: boolean = false;
  errorMessage: string = '';
  selectedUserType: string = 'coach'; // Default to coach
  showClientError: boolean = false;
  
  // User type options
  userTypeOptions = [
    { value: 'coach', label: "Coach / Consultant", icon: 'fitness_center', description: 'Create programs and manage clients' },
    { value: 'client', label: "Client", icon: 'person', description: 'Follow programs and track progress' }
  ];

  // Employee count options
  employeeCountOptions = [
    { value: '10', label: '1-10 employees' },
    { value: '50', label: '11-50 employees' },
    { value: '200', label: '51-200 employees' },
    { value: '500', label: '201-500 employees' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private httpService: HttpService,
    private toasterService: ToasterService,
    private uiService: UiService,
    private commonService: CommonService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.commonService.gaEvent('signup_page_load', 'signup', 'Sign-up page loaded');
  }

  private initializeForms(): void {
    // Step 1: Email validation form
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    // Step 2: Complete signup form
    this.signupForm = this.fb.group({
      email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      companyUrl: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9-]+$/)]],
      employeeCount: ['', Validators.required]
    });
  }

  // Handle user type selection
  onUserTypeChange(userType: string): void {
    if (userType === 'client') {
      // Don't actually select client, but show error message
      this.showClientError = true;
      // Keep the previous selection (coach by default)
      return;
    }
    
    this.selectedUserType = userType;
    this.showClientError = false;
    this.errorMessage = '';
  }

  // Step 1: Email validation
  onEmailSubmit(): void {
    if (this.emailForm.invalid) {
      this.markFormGroupTouched(this.emailForm);
      return;
    }

    const email = this.emailForm.get('email')?.value;
    this.validateEmail(email);
  }

  private validateEmail(email: string): void {
    this.isLoading = true;
    this.errorMessage = '';
     
    // API call to check if email exists
    this.httpService.getCall(`${config.apiUrl}/signup/check-email/${email}`).subscribe(
      (response: any) => {
        this.isLoading = false;
        
        if (!response.available) {
          // Email already exists
          this.emailExists = true;
          this.errorMessage = 'This email is already registered. Please try a different email or go to login.';
          this.commonService.gaEvent('email_exists_error', 'signup', 'Email already exists');
        } else {
          // Email is available, proceed to step 2
          this.emailExists = false;
          this.isEmailValidated = true;
          this.currentStep = 2;
          
          // Pre-fill email in signup form
          this.signupForm.patchValue({ email: email });
          
          this.commonService.gaEvent('email_validation_success', 'signup', 'Email validation successful');
        }
      },
      (error: any) => {
        this.isLoading = false;
        this.errorMessage = error?.error?.message || 'Error validating email. Please try again.';
        this.toasterService.show('error', 'Error', this.errorMessage);
        this.commonService.gaEvent('email_validation_error', 'signup', 'Email validation failed');

      }
    );
  }

  // Step 2: Complete signup
  onSignupSubmit(): void {
    if (this.signupForm.invalid) {
      this.markFormGroupTouched(this.signupForm);
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const signupData = {
      admin_email: this.emailForm.get('email')?.value,
      company_name: this.signupForm.get('companyName')?.value,
      admin_firstname: this.signupForm.get('firstName')?.value,
      admin_lastname: this.signupForm.get('lastName')?.value,
      admin_password: this.signupForm.get('password')?.value,
      company_url: this.signupForm.get('companyUrl')?.value,
      trial_client_limit: parseInt(this.signupForm.get('employeeCount')?.value) || 5
    };

    // API call to create account
    this.httpService.postCall(`${config.apiUrl}/signup`, signupData).subscribe(
      (response: any) => {
        this.isLoading = false;
        this.toasterService.show('success', 'Success', 'Account created successfully! Please check your email for verification.');
        this.commonService.gaEvent('signup_success', 'signup', 'Account created successfully');
        
        // Redirect to login page after successful signup
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      (error: any) => {
        this.isLoading = false;
        this.errorMessage = error?.error?.message || 'Error creating account. Please try again.';
        this.toasterService.show('error', 'Error', this.errorMessage);
        this.commonService.gaEvent('signup_error', 'signup', 'Account creation failed');
      }
    );
  }

  // Go back to step 1
  goBackToEmailStep(): void {
    this.currentStep = 1;
    this.isEmailValidated = false;
    this.errorMessage = '';
    this.showClientError = false;
    this.selectedUserType = 'coach';
    this.signupForm.reset();
    this.initializeForms();
  }

  // Try different email
  tryDifferentEmail(): void {
    this.emailExists = false;
    this.errorMessage = '';
    this.showClientError = false;
    this.selectedUserType = 'coach';
    this.emailForm.get('email')?.setValue('');
  }

  // Navigate to login
  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  // Form validation helpers
  isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean {
    const field = formGroup.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(formGroup: FormGroup, fieldName: string): string {
    const field = formGroup.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        const minLength = field.errors['minlength'].requiredLength;
        return `${this.getFieldLabel(fieldName)} must be at least ${minLength} characters`;
      }
      if (field.errors['pattern']) {
        if (fieldName === 'companyUrl') {
          return 'Company URL can only contain letters, numbers, and hyphens';
        }
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      email: 'Email',
      companyName: 'Company Name',
      firstName: 'First Name',
      lastName: 'Last Name',
      password: 'Password',
      companyUrl: 'Company URL',
      employeeCount: 'Number of Employees'
    };
    return labels[fieldName] || fieldName;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // Password strength validation
  getPasswordStrength(): string {
    const password = this.signupForm.get('password')?.value || '';
    if (password.length === 0) return '';
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength < 2) return 'weak';
    if (strength < 4) return 'medium';
    return 'strong';
  }

  getPasswordStrengthColor(): string {
    const strength = this.getPasswordStrength();
    switch (strength) {
      case 'weak': return '#f44336';
      case 'medium': return '#ff9800';
      case 'strong': return '#4caf50';
      default: return '#ddd';
    }
  }
}